import { z } from 'zod'

export const encounterTypeOptions = [
  { value: 'phone-call', label: 'Phone Call' },
  { value: 'video-call', label: 'Video Call' },
] as const

export const reasonForCallOptions = [
  { value: 'result-follow-up', label: 'Result Follow-up' },
  { value: 'scheduling', label: 'Scheduling' },
  { value: 'missed-appointment', label: 'Missed Appointment' },
  { value: 'instructions-education', label: 'Instructions / Education' },
  { value: 'insurance-question', label: 'Insurance Question' },
  { value: 'general-outreach', label: 'General Outreach' },
] as const

export const outcomeOptions = [
  { value: 'completed', label: 'Completed' },
  { value: 'follow-up-needed', label: 'Follow-up Needed' },
  { value: 'appointment-scheduled', label: 'Appointment Scheduled' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'no-answer', label: 'No Answer' },
  { value: 'voicemail-left', label: 'Voicemail Left' },
] as const

export const careCoordinatorNoteSchema = z
  .object({
    encounterType: z.enum(['phone-call', 'video-call']),
    reasonForCall: z.enum([
      'result-follow-up',
      'scheduling',
      'missed-appointment',
      'instructions-education',
      'insurance-question',
      'general-outreach',
    ]),
    callDurationSeconds: z.number().int().nonnegative(),
    quickNote: z.string().trim().min(1, 'Quick note is required'),
    outcome: z.enum([
      'completed',
      'follow-up-needed',
      'appointment-scheduled',
      'escalated',
      'no-answer',
      'voicemail-left',
    ]),
    followUpNeeded: z.enum(['yes', 'no']),
    followUpDate: z.string().optional(),
  })
  .refine((v) => v.followUpNeeded !== 'yes' || (v.followUpDate && v.followUpDate.length > 0), {
    path: ['followUpDate'],
    message: 'Follow-up date is required when follow-up is needed',
  })

export type CareCoordinatorNoteFormValues = z.infer<typeof careCoordinatorNoteSchema>

export function getEncounterTypeLabel(
  value: CareCoordinatorNoteFormValues['encounterType'],
): string {
  return encounterTypeOptions.find((o) => o.value === value)?.label ?? value
}

export function getReasonForCallLabel(
  value: CareCoordinatorNoteFormValues['reasonForCall'],
): string {
  return reasonForCallOptions.find((o) => o.value === value)?.label ?? value
}

export function getOutcomeLabel(value: CareCoordinatorNoteFormValues['outcome']): string {
  return outcomeOptions.find((o) => o.value === value)?.label ?? value
}

/** Same M:SS display as the call timer in CareCoordinatorNote. */
export function formatCareCoordinatorCallDuration(totalSeconds: number): string {
  const m = Math.floor(totalSeconds / 60)
  const s = Math.abs(totalSeconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}
